import type { IStoreCartLineItemSchema } from '@r/types/zod-schemas.ts';
import type { z } from 'zod';
import { DeleteButton } from '@client/components/cart/delete-button.tsx';
import CartItemSelect from '@client/components/cart/cart-item-select.tsx';
import defaultProductImage from '@r/assets/images/fiddy/default-product-image.png';
import { CartContext } from '@r/context/cart-context.ts';
import { type ReactNode, useContext } from 'react';
import { Link } from '@tanstack/react-router';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@ui/card.tsx';
import { CoinsIcon } from 'lucide-react';
import pointsTranslation from '@r/translations/points-translation.ts';
import { useTranslation } from 'react-i18next';
import { FormattedHtml } from '@r/components/formatted-html.tsx';
import { isOutOfStock } from '@r/utils/product-stock.ts';
import { cn } from '@r/utils/utils.ts';
import { toast } from 'sonner';

type ItemProps = {
  item: z.infer<typeof IStoreCartLineItemSchema>;
  isUnavailable: boolean;
};

const ItemLink = ({ children, to }: { children: ReactNode; to: string }) => (
  <Link to={to} className="hover:underline">
    {children}
  </Link>
);

const Item = ({ item, isUnavailable }: ItemProps) => {
  const { t } = useTranslation();
  const { updateCartItemQuantity } = useContext(CartContext);
  const outOfStock = isOutOfStock(item.product.stock);

  if (!item.thumbnail && !item.product.images?.length) {
    item.thumbnail = defaultProductImage;
  }

  const totalOriginalPrice = item.total;
  const totalDiscountAmount = item.is_discountable ? item.discount_total : 0;
  totalDiscountAmount > 0 ? Math.round((totalDiscountAmount / totalOriginalPrice) * 100) : null;

  return (
    <Card
      className={cn(
        'border-none sm:grid grid-cols-12 overflow-hidden sm:min-h-60',
        isUnavailable && 'border border-destructive/30 bg-destructive/5'
      )}
    >
      <div className="col-span-3 flex items-center justify-center p-6 relative">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-t sm:bg-gradient-to-l from-muted-foreground/10 to-transparent to-50%" />
        <ItemLink to={item.product_iri}>
          <img
            className="object-cover object-center w-full max-h-[200px]"
            src={item.product.images?.[0]?.pathUri || defaultProductImage}
            alt={item.product.name}
            onError={(e) => {
              e.currentTarget.src = defaultProductImage;
              e.currentTarget.onerror = null;
            }}
          />
        </ItemLink>
      </div>

      <div className="col-span-9 flex flex-col justify-between">
        <CardHeader>
          <CardTitle className="text-xl">
            <ItemLink to={item.product_iri}>
              <FormattedHtml as="span" text={item.product.name} />
              &nbsp;
              {item.dynamic_price_amount &&
                t('item.dynamic.price', {
                  amount: item.dynamic_price_amount,
                  currency: '€'
                })}
            </ItemLink>
            <FormattedHtml
              text={item.product.category?.label}
              className="text-xs text-primary/60 line-clamp-2"
            />
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-1">
          <p className="text-sm text-muted-foreground">{t('cart.item.quantity.label')}</p>

          <div className="flex gap-5">
            <CartItemSelect
              value={String(item.quantity)}
              className="rounded-full"
              onValueChange={(value) => {
                if (!item.dynamic_price_amount) {
                  updateCartItemQuantity({
                    cartItem: item,
                    quantity: Number.parseInt(value, 10)
                  }).then((result) => {
                    if (!result.success) {
                      toast.error(result.error ?? t('cart.error.stock.insufficient'));
                    }
                  });
                }
              }}
              disabled={!!item.dynamic_price_amount || outOfStock}
              maxQuantity={item.product.stock ?? 100}
            />

            <DeleteButton
              cartItem={item}
              variant="outline"
              size="default"
              className="text-sm rounded-full"
            >
              {t('cart.item.action.remove')}
            </DeleteButton>
          </div>
          {isUnavailable && (
            <p className="text-sm font-medium text-destructive">
              {t('cart.error.stock.insufficient')}
            </p>
          )}
        </CardContent>

        <CardFooter className="flex-col items-start space-y-1">
          <p className="text-sm text-muted-foreground">{t('cart.item.amount.label')}</p>

          <p className="flex gap-x-4 font-bold">
            <CoinsIcon size={20} />
            {pointsTranslation(t, item.total, true)}
          </p>
        </CardFooter>
      </div>
    </Card>
  );
};

export { Item };
